import { useState } from "react";
import { ChevronDown, Download, ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { eur } from "@/lib/format";
import { fechaCorta } from "@/components/contratos/constants";

const BADGE = {
  pendiente: ["Pendiente de tu respuesta", "bg-amber-50 text-amber-700 border-amber-200"],
  aprobado: ["Aprobado", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  rechazado: ["Rechazado", "bg-rose-50 text-rose-700 border-rose-200"],
};

const Detalle = ({ p }) => (
  <div className="border-t border-slate-100 px-4 sm:px-5 py-4 space-y-4" data-testid={`presupuesto-detalle-${p.id}`}>
    {p.capitulos.map((c, i) => (
      <div key={c.id ?? i}>
        <p className="text-sm font-bold text-[#1B2B4B] mb-1.5">{c.nombre || `Capítulo ${i + 1}`}</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm min-w-[420px]">
            <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="py-1.5 pr-2 font-semibold">Concepto</th><th className="py-1.5 px-2 font-semibold text-right">Cant.</th><th className="py-1.5 px-2 font-semibold text-right">Precio</th><th className="py-1.5 pl-2 font-semibold text-right">Importe</th></tr></thead>
            <tbody>{c.partidas.map((x, j) => (
              <tr key={x.id ?? j} className="border-b border-slate-50"><td className="py-1.5 pr-2 text-slate-700">{x.descripcion}</td>
                <td className="py-1.5 px-2 text-right whitespace-nowrap">{x.cantidad} {x.unidad || ""}</td>
                <td className="py-1.5 px-2 text-right whitespace-nowrap">{eur(x.precio_unitario)}</td>
                <td className="py-1.5 pl-2 text-right whitespace-nowrap font-semibold">{eur(x.importe)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    ))}
    <dl className="ml-auto max-w-xs text-sm space-y-1">
      <div className="flex justify-between"><dt className="text-slate-500">Base imponible</dt><dd>{eur(p.base_imponible)}</dd></div>
      {(p.iva_desglose || []).map((d) => <div key={d.pct} className="flex justify-between"><dt className="text-slate-500">IVA {d.pct} %</dt><dd>{eur(d.cuota)}</dd></div>)}
      {p.irpf_amount > 0 && <div className="flex justify-between"><dt className="text-slate-500">IRPF</dt><dd>-{eur(p.irpf_amount)}</dd></div>}
      <div className="flex justify-between border-t-2 border-[#00B4CC] pt-1 font-extrabold text-[#1B2B4B]"><dt>Total</dt><dd>{eur(p.total)}</dd></div>
    </dl>
    {p.plan_pagos?.length > 0 && <p className="text-xs text-slate-500">Forma de pago: {p.plan_pagos.map((x) => `${x.concepto} ${x.pct} %`).join(" · ")}</p>}
    {p.notas && <p className="text-xs text-slate-500 whitespace-pre-line">{p.notas}</p>}
  </div>
);

export const PresupuestoPortal = ({ p, onResponder, onPdf }) => {
  const [abierto, setAbierto] = useState(p.estado_cliente === "pendiente");
  const [txt, cls] = BADGE[p.estado_cliente];
  return (
    <article className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden" data-testid={`presupuesto-portal-${p.id}`}>
      <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center gap-3">
        <button onClick={() => setAbierto(!abierto)} className="flex-1 min-w-[180px] text-left flex items-center gap-2" data-testid={`presupuesto-toggle-${p.id}`}>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${abierto ? "rotate-180" : ""}`} />
          <span><span className="block font-bold text-[#1B2B4B]">Presupuesto {p.numero}</span><span className="block text-xs text-slate-500">{fechaCorta(p.fecha)}</span></span>
        </button>
        <span className="text-lg font-extrabold text-[#1B2B4B]">{eur(p.total)}</span>
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${cls}`} data-testid={`presupuesto-estado-${p.id}`}>{txt}</span>
      </div>
      {abierto && <Detalle p={p} />}
      <div className="px-4 sm:px-5 py-3 bg-slate-50 flex flex-wrap gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={() => onPdf(p)} data-testid={`presupuesto-pdf-${p.id}`}><Download className="w-4 h-4 mr-1" /> PDF</Button>
        {p.estado_cliente === "pendiente" && <>
          <Button size="sm" variant="outline" onClick={() => onResponder("rechazar", p)} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid={`presupuesto-rechazar-${p.id}`}><ThumbsDown className="w-4 h-4 mr-1" /> Rechazar</Button>
          <Button size="sm" onClick={() => onResponder("aprobar", p)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`presupuesto-aprobar-${p.id}`}><ThumbsUp className="w-4 h-4 mr-1" /> Aprobar</Button>
        </>}
      </div>
    </article>
  );
};
